/**
 * Makes the Vipps MobilePay "Hurtigkasse" express button on a product page buy the whole package,
 * not just the bed.
 *
 * THE BUG
 * The bundle lives entirely in this theme: the bed is the product form's own line, and the
 * mattress — plus, on the Gamer ULTIMATE loftsenger, the sovesofa — is added by product-form.js
 * when the customer presses "Legg i handlekurv". The Vipps button is a separate app block
 * (shopify://apps/vippsmobilepay-expresscheckout), rendered OUTSIDE the form, and it builds its
 * express checkout from the form's single `id` + `quantity`. So a customer on "Seng + madrass" —
 * the package that is preselected — who pressed Vipps paid for the bed alone and never got the
 * mattress, at the bed's own price. That is the whole reported bug.
 *
 * WHY WE SEED THE CART INSTEAD OF PATCHING THEIR BUTTON
 * hurtigkasse.js does have a registry of "bundle adapters" for third-party bundle apps, but it is
 * closed over inside their IIFE and cannot be extended from the theme. What it does commit to is
 * the rule it uses on a product page: the checkout it opens is `everything already in the cart`
 * plus `the form's line`, and it re-reads /cart.js at click time specifically so that it picks up
 * a cart that changed after page load. So the supported way to get the mattress into a Vipps
 * express checkout is to put it in the cart before their handler runs.
 *
 * HOW
 * A capture-phase listener on the document sees the click before the app's own listener, which is
 * bound on the button itself. When the chosen package has add-ons we stop the event, POST the
 * add-ons — and only the add-ons, because the bed is Vipps' own line and adding it here would
 * double it — to /cart/add.js, then replay the click so the app runs its normal flow against the
 * updated cart. On "Kun seng" there are no add-ons and the click is never touched.
 *
 * IF THE CART ADD FAILS we let the click through anyway rather than leaving the customer on a
 * dead button: they get the bed-only checkout they got before this file existed, which is no
 * worse than the old behaviour.
 *
 * ONE KNOWN ROUGH EDGE: the add-ons are in the cart from the moment Vipps is pressed, so a
 * customer who abandons the Vipps flow comes back to a cart holding the mattress without the bed.
 * That is the price of the only integration point the app gives us, and it is recoverable by the
 * customer; silently shipping a bed without its mattress is not.
 *
 * DO NOT "fix" this by skipping add-ons the cart already holds. A customer who added the package
 * normally and then pressed Vipps gets a second bed either way — the app always stacks its PDP
 * line on top of the cart — so the add-ons have to stack with it or the counts drift apart and
 * they end up with two beds and one mattress.
 */

/** The Vipps app block, product-page instances only — the cart/drawer button reads the cart already. */
const HURTIGKASSE_WRAPPER_SELECTOR = '[data-hurtigkasse][data-is-product-page="true"]';
const HURTIGKASSE_BUTTON_SELECTOR = '.hurtigkasse-btn';

/** Set while we replay a click we have already handled, so we don't intercept our own event. */
let bundleExpressReplaying = false;

/** Set while the add-ons are on their way to the cart, so a double click doesn't add them twice. */
let bundleExpressInFlight = false;

function bundleExpressCartAddUrl() {
  try {
    return Theme.routes.cart_add_url || '/cart/add.js';
  } catch (error) {
    return '/cart/add.js';
  }
}

/**
 * The product form the bundle selector belongs to. Found via the bundle block (it names its form
 * and product) rather than by taking the first `product-form-component` on the page, because
 * product cards further down (recommendations, quick add) render forms of their own.
 * @returns {(Element & { getBundleAddonItems?: () => Array<object> }) | null}
 */
function bundleExpressProductForm() {
  const bundle = /** @type {HTMLElement | null} */ (document.querySelector('dr-bundle-selector'));
  if (!bundle) return null;
  const scope = bundle.closest('.shopify-section, dialog') ?? document;
  return scope.querySelector(`product-form-component[data-product-id="${bundle.dataset.productId}"]`);
}

/**
 * @param {Array<object>} items
 * @returns {Promise<void>}
 */
async function bundleExpressAddAddons(items) {
  const response = await fetch(bundleExpressCartAddUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });

  const data = await response.json().catch(() => ({}));

  // /cart/add.js reports a rejected line (sold out, quantity rule) with a `status` field and a 4xx.
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || `cart/add responded ${response.status}`);
  }
}

/** @param {MouseEvent} event */
function bundleExpressOnClick(event) {
  if (bundleExpressReplaying) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest(HURTIGKASSE_BUTTON_SELECTOR);
  if (!button?.closest(HURTIGKASSE_WRAPPER_SELECTOR)) return;

  const addons = bundleExpressProductForm()?.getBundleAddonItems?.() ?? [];
  if (!addons.length) return;

  // From here the app's own click handler must not run until the cart holds the add-ons.
  event.preventDefault();
  event.stopImmediatePropagation();

  if (bundleExpressInFlight) return;
  bundleExpressInFlight = true;
  button.setAttribute('aria-busy', 'true');

  bundleExpressAddAddons(addons)
    .catch((error) => {
      console.error('Bundle express checkout: could not add the package add-ons to the cart.', error);
    })
    .finally(() => {
      bundleExpressInFlight = false;
      button.removeAttribute('aria-busy');

      // Hand the click back to the app. Dispatch is synchronous, so clearing the flag right after
      // is safe: their handler has already read the event by the time click() returns.
      bundleExpressReplaying = true;
      if (button instanceof HTMLElement) button.click();
      bundleExpressReplaying = false;
    });
}

document.addEventListener('click', bundleExpressOnClick, { capture: true });
